import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { crafterUrl } from '../utils/urls';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Send, 
  ChevronLeft, 
  User,
  Clock,
  Check,
  CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

export default function MessagesPage({ embedded = false }) {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const queryConversationId = searchParams.get('conversation');
  const queryCrafterId = searchParams.get('crafter');
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { markMessagesAsRead, fetchUnreadMessages } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [messageLightboxImage, setMessageLightboxImage] = useState(null);
  const [topOffset, setTopOffset] = useState(112);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const currentConvIdRef = useRef(null);

  // Calcola offset dinamico = altezza reale dello stack header (banner lancio + nav + categorie).
  // Misura la posizione del <main> che segue subito gli header: robusto a responsive e banner.
  useEffect(() => {
    const computeOffset = () => {
      const mainEl = document.querySelector('main');
      if (mainEl) {
        const offset = mainEl.getBoundingClientRect().top + window.scrollY;
        if (offset > 0) {
          setTopOffset(offset);
          return;
        }
      }
      setTopOffset(112);
    };
    computeOffset();
    const raf = requestAnimationFrame(computeOffset);
    window.addEventListener('resize', computeOffset);
    const t = setTimeout(computeOffset, 300);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', computeOffset);
      clearTimeout(t);
    };
  }, []);

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const getAccessToken = () => {
    const sessionData = localStorage.getItem('sb-' + supabaseUrl.replace('https://', '').split('.')[0] + '-auth-token');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.access_token) return parsed.access_token;
      } catch (e) {}
    }
    return supabaseKey;
  };

  // Render message content with images, links, and formatting
  const renderMessageContent = (content, isOwn, onImageClick) => {
    if (!content) return null;
    
    const elements = [];
    const lines = content.split('\n');
    
    lines.forEach((line, lineIndex) => {
      // Check for image marker [IMG:url]
      const imgMatch = line.match(/\[IMG:([^\]]+)\]/);
      if (imgMatch) {
        const imageUrl = imgMatch[1];
        elements.push(
          <img
            key={`img-${lineIndex}`}
            src={imageUrl}
            alt="Immagine allegata"
            className="max-w-[200px] max-h-[200px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity my-1 object-cover"
            onClick={() => onImageClick && onImageClick(imageUrl)}
          />
        );
        return;
      }
      
      // Process text line for links and bold
      let processedLine = line;
      const parts = [];
      let lastIndex = 0;
      
      // Match markdown links: [text](url)
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      let match;
      
      while ((match = linkRegex.exec(line)) !== null) {
        // Add text before the link
        if (match.index > lastIndex) {
          parts.push({ type: 'text', content: line.slice(lastIndex, match.index) });
        }
        
        parts.push({ type: 'link', text: match[1], url: match[2] });
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < line.length) {
        parts.push({ type: 'text', content: line.slice(lastIndex) });
      }
      
      // If no special content, just add the line
      if (parts.length === 0 && line.trim()) {
        parts.push({ type: 'text', content: line });
      }
      
      // Render parts
      const lineElements = parts.map((part, partIndex) => {
        if (part.type === 'link') {
          return (
            <a
              key={`link-${lineIndex}-${partIndex}`}
              href={part.url}
              target="_self"
              className={`inline-block my-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isOwn 
                  ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30' 
                  : 'bg-shop-primary hover:bg-shop-primary-dark text-white'
              }`}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = part.url;
              }}
            >
              {part.text}
            </a>
          );
        }
        
        // Handle bold and italic text
        let textContent = part.content;
        
        // Bold **text**
        const boldParts = textContent.split(/\*\*(.+?)\*\*/g);
        const formattedParts = boldParts.map((bp, j) => {
          if (j % 2 === 1) {
            return <strong key={`bold-${lineIndex}-${partIndex}-${j}`}>{bp}</strong>;
          }
          // Italic *text*
          const italicParts = bp.split(/\*(.+?)\*/g);
          return italicParts.map((ip, k) => 
            k % 2 === 1 ? <em key={`italic-${lineIndex}-${partIndex}-${j}-${k}`}>{ip}</em> : ip
          );
        });
        
        return <span key={`text-${lineIndex}-${partIndex}`}>{formattedParts}</span>;
      });
      
      if (lineElements.length > 0) {
        elements.push(
          <div key={`line-${lineIndex}`}>
            {lineElements}
          </div>
        );
      } else if (line === '') {
        elements.push(<br key={`br-${lineIndex}`} />);
      }
    });
    
    return elements;
  };

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    // 1) URL path :conversationId ha priorità
    // 2) Query param ?conversation=<id>
    // 3) Query param ?crafter=<id> → cerca o crea una conversazione con quell'artigiano
    const targetConvId = conversationId || queryConversationId;

    if (targetConvId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === targetConvId);
      if (conv) {
        const isNewConversation = currentConvIdRef.current !== targetConvId;
        currentConvIdRef.current = targetConvId;
        setActiveConversation(conv);
        loadMessages(targetConvId, isNewConversation);
        loadOtherUser(conv);
      }
      return;
    }

    // Nuovo: apri o crea conversazione con crafter specifico
    if (queryCrafterId && user && conversations.length >= 0) {
      const existingConv = conversations.find(c =>
        (c.participant_1 === user.id && c.participant_2 === queryCrafterId) ||
        (c.participant_2 === user.id && c.participant_1 === queryCrafterId)
      );

      if (existingConv) {
        // Naviga alla conversazione esistente e pulisci query param
        navigate(`/messages/${existingConv.id}`, { replace: true });
      } else if (user.id !== queryCrafterId) {
        // Crea nuova conversazione
        (async () => {
          try {
            const resp = await fetch(`${supabaseUrl}/rest/v1/conversations`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                apikey: supabaseKey,
                Authorization: `Bearer ${getAccessToken()}`,
                Prefer: 'return=representation',
              },
              body: JSON.stringify({
                participant_1: user.id,
                participant_2: queryCrafterId,
              }),
            });
            const data = await resp.json();
            if (resp.ok && data && data[0]?.id) {
              await loadConversations();
              navigate(`/messages/${data[0].id}`, { replace: true });
            }
          } catch (e) {
            console.error('Failed to create conversation from ?crafter:', e);
          }
        })();
      }
    }
  }, [conversationId, queryConversationId, queryCrafterId, conversations, user]);

  // Scroll to bottom - removed, handled in loadMessages
  // No automatic scroll on messages change to prevent jumping

  // Polling for new messages - don't touch scroll position
  useEffect(() => {
    if (!activeConversation) return;
    
    const interval = setInterval(() => {
      loadMessages(activeConversation.id, false);
    }, 5000);

    return () => clearInterval(interval);
  }, [activeConversation]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/conversations?or=(participant_1.eq.${user.id},participant_2.eq.${user.id})&order=last_message_at.desc`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${getAccessToken()}`
          }
        }
      );
      
      const data = await response.json();
      if (Array.isArray(data)) {
        // Load other participant info and unread count for each conversation
        const conversationsWithUsers = await Promise.all(data.map(async (conv) => {
          const otherParticipantId = conv.participant_1 === user.id ? conv.participant_2 : conv.participant_1;
          
          // Get other user info
          const userResponse = await fetch(
            `${supabaseUrl}/rest/v1/profiles?id=eq.${otherParticipantId}&select=id,full_name,avatar_url,storefront_title`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${supabaseKey}`
              }
            }
          );
          const userData = await userResponse.json();
          
          // Get unread messages count for this conversation
          const unreadResponse = await fetch(
            `${supabaseUrl}/rest/v1/messages?conversation_id=eq.${conv.id}&sender_id=neq.${user.id}&read=eq.false&select=id`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${getAccessToken()}`
              }
            }
          );
          const unreadData = await unreadResponse.json();
          const unreadCount = Array.isArray(unreadData) ? unreadData.length : 0;
          
          // Get last message preview
          const lastMsgResponse = await fetch(
            `${supabaseUrl}/rest/v1/messages?conversation_id=eq.${conv.id}&order=created_at.desc&limit=1&select=content,sender_id,created_at`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${getAccessToken()}`
              }
            }
          );
          const lastMsgData = await lastMsgResponse.json();
          const lastMessage = Array.isArray(lastMsgData) && lastMsgData.length > 0 ? lastMsgData[0] : null;
          
          return {
            ...conv,
            other_user: Array.isArray(userData) && userData.length > 0 ? userData[0] : null,
            unread_count: unreadCount,
            last_message: lastMessage
          };
        }));
        setConversations(conversationsWithUsers);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId, scrollToBottom = false) => {
    try {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/messages?conversation_id=eq.${convId}&order=created_at.asc`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${getAccessToken()}`
          }
        }
      );
      
      const data = await response.json();
      if (Array.isArray(data)) {
        // Only update if this is still the active conversation
        if (currentConvIdRef.current === convId) {
          setMessages(data);
          markConversationAsRead(convId);
          
          if (scrollToBottom && messagesContainerRef.current) {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
              if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
              }
            }, 100);
          }
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadOtherUser = async (conv) => {
    const otherParticipantId = conv.participant_1 === user.id ? conv.participant_2 : conv.participant_1;
    try {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/profiles?id=eq.${otherParticipantId}&select=*`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`
          }
        }
      );
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setOtherUser(data[0]);
      }
    } catch (error) {
      console.error('Error loading other user:', error);
    }
  };

  const markConversationAsRead = async (convId) => {
    try {
      await fetch(
        `${supabaseUrl}/rest/v1/messages?conversation_id=eq.${convId}&sender_id=neq.${user.id}&read=eq.false`,
        {
          method: 'PATCH',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ read: true })
        }
      );
      // Update notification count
      if (fetchUnreadMessages) {
        fetchUnreadMessages();
      }
      // Update local conversation unread count
      setConversations(prev => prev.map(c => 
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    setSending(true);
    try {
      // Send message
      const response = await fetch(
        `${supabaseUrl}/rest/v1/messages`,
        {
          method: 'POST',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify({
            conversation_id: activeConversation.id,
            sender_id: user.id,
            content: newMessage.trim()
          })
        }
      );

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setMessages([...messages, data[0]]);
        // Scroll to bottom after sending
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 50);
      }

      // Update conversation last_message_at
      await fetch(
        `${supabaseUrl}/rest/v1/conversations?id=eq.${activeConversation.id}`,
        {
          method: 'PATCH',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ last_message_at: new Date().toISOString() })
        }
      );

      // Send email notification to recipient (async, don't block UI)
      if (otherUser?.email) {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API_URL}/api/email/new-message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient_email: otherUser.email,
            recipient_name: otherUser.full_name || otherUser.business_name || 'Utente',
            sender_name: profile?.full_name || profile?.business_name || 'Utente',
            message_preview: newMessage.trim().substring(0, 150),
            conversation_id: activeConversation.id
          })
        }).catch(err => console.error('Error sending message notification email:', err));
      }

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ieri';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('it-IT', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Accedi per vedere i messaggi</h2>
          <Link to="/login">
            <Button className="bg-shop-primary text-white">Accedi</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 bg-slate-50"
      style={{ top: `${topOffset}px` }}
      data-testid="messages-page"
    >
      <div className="h-full max-w-6xl mx-auto flex">
        {/* Conversations List - Sidebar */}
        <div className={`w-full md:w-80 bg-white border-r flex-shrink-0 flex flex-col h-full ${activeConversation ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b flex-shrink-0">
            <h2 className="text-xl font-bold text-slate-800">Messaggi</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shop-primary mx-auto"></div>
            </div>
          ) : conversations.length > 0 ? (
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conv) => {
                const hasUnread = conv.unread_count > 0;
                const lastMsgPreview = conv.last_message?.content 
                  ? (conv.last_message.content.length > 40 
                      ? conv.last_message.content.substring(0, 40).replace(/\*\*/g, '') + '...' 
                      : conv.last_message.content.replace(/\*\*/g, ''))
                  : '';
                const isOwnLastMsg = conv.last_message?.sender_id === user.id;
                
                return (
                  <button
                    key={conv.id}
                    onClick={() => navigate(`/messages/${conv.id}`)}
                    className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors border-b ${
                      activeConversation?.id === conv.id 
                        ? 'bg-shop-primary/5 border-l-4 border-l-shop-primary' 
                        : hasUnread 
                          ? 'bg-shop-primary/5' 
                          : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-shop-primary flex items-center justify-center text-white font-bold flex-shrink-0">
                        {conv.other_user?.avatar_url ? (
                          <img src={conv.other_user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          (conv.other_user?.storefront_title || conv.other_user?.full_name || 'U')[0].toUpperCase()
                        )}
                      </div>
                      {hasUnread && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {conv.unread_count > 9 ? '9+' : conv.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`truncate ${hasUnread ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>
                          {conv.other_user?.storefront_title || conv.other_user?.full_name || 'Utente'}
                        </p>
                        <span className={`text-xs flex-shrink-0 ${hasUnread ? 'text-shop-primary font-semibold' : 'text-slate-400'}`}>
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      {lastMsgPreview && (
                        <p className={`text-sm truncate ${hasUnread ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                          {isOwnLastMsg && <span className="text-slate-400">Tu: </span>}
                          {lastMsgPreview}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Nessuna conversazione</p>
              <p className="text-sm text-slate-500 mt-1">
                Inizia una chat dal profilo di un artigiano
              </p>
            </div>
          )}
        </div>

        {/* Chat Area - Main Content */}
        <div className={`flex-1 flex flex-col h-full min-h-0 ${!activeConversation ? 'hidden md:flex' : 'flex'}`}>
          {activeConversation ? (
            <>
              {/* Chat Header - Fixed height */}
              <div className="p-4 border-b bg-white flex items-center gap-3 flex-shrink-0">
                <button 
                  onClick={() => { setActiveConversation(null); navigate('/messages'); }}
                  className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                {otherUser && (
                  <Link to={otherUser.is_crafter_enabled ? crafterUrl(otherUser) : '#'} className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-shop-primary flex items-center justify-center text-white font-bold">
                      {otherUser.avatar_url ? (
                        <img src={otherUser.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        (otherUser.storefront_title || otherUser.full_name || 'U')[0].toUpperCase()
                      )}
                    </div>
                    <div>
                      <p className="font-semibold">{otherUser.storefront_title || otherUser.full_name}</p>
                      <p className="text-xs text-slate-500">{otherUser.is_crafter_enabled ? 'Artigiano' : 'Cliente'}</p>
                    </div>
                  </Link>
                )}
              </div>

              {/* Messages List - Scrollable, takes remaining space */}
              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 min-h-0"
              >
                <AnimatePresence>
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === user.id;
                    // Messaggi di sistema generati dal modulo Assistenza ordine
                    if (msg.message_type === 'system_case') {
                      const caseId = msg.metadata?.case_id;
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex justify-center"
                          data-testid={`system-message-${msg.id}`}
                        >
                          <div className="max-w-[85%] rounded-2xl bg-gradient-to-r from-shop-primary/10 to-[#00CED1]/8 border border-shop-primary/20 px-4 py-3">
                            <div className="whitespace-pre-wrap text-sm text-slate-700">
                              {msg.content.replace(/\*\*/g, '')}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-[11px] text-slate-400">Rarifex · {formatTime(msg.created_at)}</span>
                              {caseId && (
                                <Link
                                  to={`/assistenza-ordine/${caseId}`}
                                  className="text-[11px] font-semibold text-shop-primary hover:underline"
                                >
                                  Vedi assistenza ordine →
                                </Link>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    }
                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? 'order-1' : 'order-2'}`}>
                          <div className={`rounded-2xl px-4 py-2 ${
                            isOwn 
                              ? 'bg-shop-primary text-white rounded-br-sm' 
                              : 'bg-white text-slate-800 rounded-bl-sm shadow-sm'
                          }`}>
                            <div className="whitespace-pre-wrap">{renderMessageContent(msg.content, isOwn, setMessageLightboxImage)}</div>
                          </div>
                          <div className={`flex items-center gap-1 mt-1 text-xs text-slate-400 ${isOwn ? 'justify-end' : ''}`}>
                            <span>{formatTime(msg.created_at)}</span>
                            {isOwn && (
                              msg.read ? <CheckCheck className="w-3 h-3 text-shop-primary" /> : <Check className="w-3 h-3" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input - Fixed at bottom */}
              <form onSubmit={handleSendMessage} className="p-4 bg-white border-t flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={!newMessage.trim() || sending}
                    className="bg-shop-primary text-white"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-50">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">I tuoi messaggi</h3>
                <p className="text-slate-500">Seleziona una conversazione per iniziare</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Message Image Lightbox */}
      <Dialog open={!!messageLightboxImage} onOpenChange={(open) => !open && setMessageLightboxImage(null)}>
        <DialogContent className="max-w-4xl w-auto p-2 bg-black/95 border-none">
          <img 
            src={messageLightboxImage} 
            alt="Immagine ingrandita" 
            className="max-w-full max-h-[85vh] rounded-lg mx-auto"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
